import type { PagedResponse } from 'types/paged-response'
import type { ScenarioRunResponse } from 'types/scenarios/runs/scenario-runs'

export type GameDayParticipantPosition =
  | 'Owner'
  | 'Coordinator'
  | 'Observer'
  | 'Reporter'

export interface GameDayParticipant {
  position: GameDayParticipantPosition
  email?: string
}
export type GameDayType =
  | 'Exploratory'
  | 'TrainTeams'
  | 'ValidatePastOutages'
  | 'TuneObservability'
  | 'Other'

export interface GameDaySummaryRequest {
  notes?: string
  actions?: string
}

export interface GameDayPlanRequest {
  name: string
  goal?: string
  type?: GameDayType
  participants?: GameDayParticipant[]
  scenarioIds?: string[]
  environment?: string
  runbooks?: string[]
  dashboards?: string[]
}

export interface GameDayScenarioSummary {
  guid: string
  name: string
}

export interface GameDaySummary {
  createdBy: string
  createdAt: Date | string
  updatedAt: Date | string
  notes?: string
  actions?: string
  gameDayImages?: string[]
}

export interface GameDayPlan {
  name: string
  createdAt: Date | string
  updatedAt: Date | string
  goal?: string
  type?: GameDayType
  participants?: GameDayParticipant[]
  scenarios?: GameDayScenarioSummary[]
  environment?: string
  runbooks?: string[]
  dashboards?: string[]
}

type GameDayExecutionResultType =
  | 'Passed'
  | 'Failed'
  | 'Unsure'
  | 'Completed_Input_Results'
  | 'Failed_Input_Results'
  | 'Halted_Input_Results'
  | 'SetupFailed_Input_Results'
  | 'Setup'
  | 'Active'

export interface SourceInfo {
  requestSource?: RequestSource
  detail?: SourceDetail
}

export enum RequestSource {
  WebApp = 'WebApp',
  Api = 'Api',
  Scheduled = 'Scheduled',
  Manual = 'Manual',
  Spinnaker = 'Spinnaker',
  Concord = 'Concord',
  GremlinFeeder = 'GremlinFeeder',
  GremlinSdkPython = 'GremlinSdkPython',
  Scenario = 'Scenario',
  K8sBackend = 'K8sBackend',
  Chao = 'Chao',
  Website = 'Website',
  // Will be included in future update: https://gremlininc.atlassian.net/browse/EN-7031
  RunAll = 'RunAll'
}

export enum SourceDetail {
  Onboarding = 'Onboarding'
}

export interface GameDayExecutionResult {
  scenario: GameDayScenarioSummary
  createdBy: string
  createdAt: Date | string
  updatedAt: Date | string
  updatedBy: string
  runs: ScenarioRunResponse[]
  lastResult: GameDayExecutionResultType // latest result of the scenario
  lastResultAt: Date | string
  lastRunAt: Date | string
  lastRunVersion: number
}

export interface GameDayExecution {
  createdAt: Date | string
  createdBy: string
  lastRunAt: Date | string // timestamp of latest run of the gameDay
  results: GameDayExecutionResult[]
}

export interface GameDay {
  guid: string
  teamId: string
  createdAt: Date | string
  updatedAt: Date | string
  plan: GameDayPlan
  execution?: GameDayExecution
  summary?: GameDaySummary
}

export type PagedGameDayResponse = PagedResponse<GameDay>
